/**
 * The two navigation surfaces, over one source of truth.
 *
 * A bottom bar for the places a learner goes constantly, and a menu for
 * everything. They read from the same `Destination` and call the same `go`, so
 * they cannot disagree about where you are — which is the failure the brief
 * names, and the reason they are one file rather than two.
 *
 * The bar is limited to five items on purpose. A sixth turns a glanceable row
 * into a thing to read, and on an iPad in portrait it stops meeting the 44px
 * target without shrinking something else.
 */

import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from './Icon';
import { IconButton } from './Button';
import type { Destination } from '../auth/routes';
import { isWriting } from '../ink/writingActivity';

/**
 * Was this click a deliberate tap, or the tail of a Pencil stroke?
 *
 * A stroke that begins on the manuscript and finishes over the bottom bar ends
 * with an ordinary `click` on a tab, and the learner is thrown into another
 * screen mid-note. Safari also synthesises the compatibility mouse events after
 * `pointerup`, so the guard has to outlast the stroke itself — see
 * `WRITING_SETTLE_MS`.
 *
 * `detail` is the second half: a click with `detail === 0` was not produced by a
 * pointer at all (a keyboard Enter, or a programmatic call), and those are always
 * deliberate.
 */
function isDeliberateTap(event: { detail: number }): boolean {
  if (event.detail === 0) return true;
  return !isWriting();
}

export interface NavItem {
  destination: Destination;
  label: string;
  icon: IconName;
}

/** The most-used five. Everything else lives in the menu. */
export const BAR_ITEMS: NavItem[] = [
  { destination: 'home', label: 'Home', icon: 'home' },
  { destination: 'write', label: 'Write', icon: 'pen' },
  { destination: 'fib', label: 'FIB', icon: 'plus' },
  { destination: 'modes', label: 'Modes', icon: 'play' },
  { destination: 'learn', label: 'Learn', icon: 'book' },
];

export const MENU_ITEMS: NavItem[] = [
  ...BAR_ITEMS.slice(0, 4),
  { destination: 'intervals', label: 'Intervals', icon: 'book' },
  // The reference page. It belongs beside the two labs that send learners to
  // it, and it does not belong in the bar — five is the limit.
  { destination: 'chord-legend', label: 'Chord Legend', icon: 'book' },
  ...BAR_ITEMS.slice(4),
  { destination: 'classroom', label: 'Class', icon: 'classroom' },
];

export interface GlobalNavProps {
  current: Destination;
  go: (destination: Destination) => void;
  /** Rendered at the foot of the menu: account, sync status, about. */
  menuFooter?: React.ReactNode;
}

export function BottomNav({ current, go }: GlobalNavProps) {
  /*
   * Intervals and Class are reached from the menu, so when you are on one the
   * bar has no active item at all — which reads as "nothing is selected, the app
   * has lost its place". The menu button carries the state instead, and the bar
   * says so rather than looking broken.
   */
  const inBar = BAR_ITEMS.some((item) => item.destination === current);
  return (
    <nav className="mm-bottomnav" aria-label="Main" data-elsewhere={inBar ? undefined : 'true'}>
      {BAR_ITEMS.map((item) => {
        const active = item.destination === current;
        return (
          <button
            key={item.destination}
            type="button"
            className={`mm-bottomnav__item ${active ? 'is-active' : ''}`}
            // The accessible statement of "you are here". Selection never rests
            // on colour alone — there is an underline as well.
            aria-current={active ? 'page' : undefined}
            onClick={(event) => {
              if (!isDeliberateTap(event)) return;
              go(item.destination);
            }}
          >
            <Icon name={item.icon} size={20} />
            <span className="mm-bottomnav__label">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export function NavMenu({ current, go, menuFooter }: GlobalNavProps) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  /*
   * The wrapper, not the button — `IconButton` does not forward a ref.
   *
   * Escape used to call `.focus()` on this span, which is not focusable, so
   * focus silently fell to `<body>` and a keyboard user was stranded at the top
   * of the document every time they dismissed the menu. Reaching for the button
   * inside it is what actually returns focus where it came from.
   */
  const triggerRef = useRef<HTMLSpanElement>(null);
  const focusTrigger = () => triggerRef.current?.querySelector('button')?.focus();

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>('button')?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      // Focus goes back where it came from, or a keyboard user is stranded at
      // the top of the document.
      focusTrigger();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <>
      <span ref={triggerRef}>
        <IconButton
          icon="menu"
          label="Menu"
          aria-expanded={open}
          onClick={(event) => {
            // The trigger sits in the same header the learner writes beneath,
            // and it was the one control in the navigation without this guard:
            // a stroke ending over it opened the menu on top of the manuscript.
            // Opening a menu is not destructive, but it interrupts the note.
            if (!isDeliberateTap(event)) return;
            setOpen((value) => !value);
          }}
        />
      </span>

      {/*
        The scrim closes on `pointerdown` rather than `mousedown`: on iPad the
        compatibility mouse events arrive late and out of order relative to the
        button's click, so a tap could dismiss the panel before the item it
        landed on had navigated. Pointer events are the ones that actually
        happened.
      */}
      {open ? (
        <div className="mm-navmenu-scrim" onPointerDown={() => setOpen(false)}>
          <div
            ref={panelRef}
            className="mm-navmenu"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            onPointerDown={(event) => event.stopPropagation()}
          >
            {/* A structured list, not a pile of cards. */}
            <ul className="mm-navmenu__list">
              {MENU_ITEMS.map((item) => {
                const active = item.destination === current;
                return (
                  <li key={item.destination}>
                    <button
                      type="button"
                      className={`mm-navmenu__item ${active ? 'is-active' : ''}`}
                      aria-current={active ? 'page' : undefined}
                      onClick={(event) => {
                        if (!isDeliberateTap(event)) return;
                        go(item.destination);
                        setOpen(false);
                      }}
                    >
                      <Icon name={item.icon} size={18} />
                      <span>{item.label}</span>
                      {active ? <span className="mm-navmenu__here">You are here</span> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            {menuFooter ? <div className="mm-navmenu__footer">{menuFooter}</div> : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
